import { Scene } from "@babylonjs/core/scene";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
// 注意: スクショ実装は screenshotTools 側で提供される（tools.js 側は未import時に throw するスタブ）
import { CreateScreenshotUsingRenderTargetAsync } from "@babylonjs/core/Misc/screenshotTools";
import { loadMesh, MapModel } from "common/model";
import { GeneratedMapData } from "features/builder/api";
import { generatedMapToModels } from "features/builder/generatedMap";
import { createOuterNode } from "features/builder/canvas/scene/util";
import {
  CAMERA_CONFIG,
  LIGHT_CONFIG,
  MAP_MESH_SIZE,
  DEFAULT_MAP_SIZE,
} from "features/builder/constants";

const PREVIEW_WIDTH = 480;
const PREVIEW_HEIGHT = 360;
// 編集画面のカメラ距離は画面いっぱい用なので、プレビュー枠ではマップが小さく写る。寄せる係数
const PREVIEW_RADIUS_RATIO = 0.68;

// 同じ Engine 上で複数案を同時に描くと RenderTarget が競合するため直列化する
let queue: Promise<unknown> = Promise.resolve();

/**
 * AI生成マップ案のモデル・カメラ・ライトを指定シーンに組み立てる。
 * 静止画プレビューと、ぐるぐる回せるライブプレビューで共用する。
 */
export const buildGeneratedMapScene = (
  previewScene: Scene,
  data: GeneratedMapData,
  size: { x: number; z: number },
): Promise<ArcRotateCamera> => buildModelsScene(previewScene, generatedMapToModels(data), size);

/** マップのモデル・カメラ・ライトを指定シーンに組み立てる。withDirection で編集画面と同じ向きにする */
const buildModelsScene = async (
  previewScene: Scene,
  models: MapModel[],
  size: { x: number; z: number },
  withDirection = false,
): Promise<ArcRotateCamera> => {
  const sizeRatio = Math.max(size.x, size.z) / DEFAULT_MAP_SIZE.x;
  const camera = new ArcRotateCamera(
    "preview-camera",
    CAMERA_CONFIG.alpha,
    CAMERA_CONFIG.beta,
    CAMERA_CONFIG.radius * sizeRatio * PREVIEW_RADIUS_RATIO,
    new Vector3((size.x * MAP_MESH_SIZE) / 2, CAMERA_CONFIG.target.y, (size.z * MAP_MESH_SIZE) / 2),
    previewScene,
  );
  const light = new HemisphericLight("preview-light", new Vector3(0, 1, 0), previewScene);
  light.intensity = LIGHT_CONFIG.intensity;

  for (const model of models) {
    const mesh = await loadMesh(previewScene, model.filename, model.offset);
    mesh.getChildMeshes().forEach((subMesh) => {
      if (subMesh.name.includes("Cube")) {
        subMesh.isVisible = false;
      }
    });
    const outer = createOuterNode(
      previewScene,
      new Vector3(model.position.x, model.position.y, model.position.z),
    );
    if (model.scaling) {
      outer.scaling = new Vector3(model.scaling.x, model.scaling.y, model.scaling.z);
    }
    if (withDirection && model.direction) {
      outer.rotation = new Vector3(
        0,
        Math.atan2(Number(model.direction.x), Number(model.direction.z)),
        0,
      );
    }
    mesh.parent = outer;
  }

  return camera;
};

const render = async (
  scene: Scene,
  data: GeneratedMapData,
  size: { x: number; z: number },
): Promise<string> => {
  const engine = scene.getEngine();
  // 編集画面の Engine を使い回す（WebGL コンテキストを増やさない）。
  // プレビュー用 Scene は 1 フレームだけ描いて破棄するので描画コストは残らない。
  const previewScene = new Scene(engine);
  previewScene.clearColor = new Color4(0, 0, 0, 0);
  try {
    const camera = await buildGeneratedMapScene(previewScene, data, size);
    await previewScene.whenReadyAsync();
    return await CreateScreenshotUsingRenderTargetAsync(engine, camera, {
      width: PREVIEW_WIDTH,
      height: PREVIEW_HEIGHT,
    });
  } finally {
    previewScene.dispose();
  }
};

/**
 * AI生成マップ案を実際の 3D モデルで 1 枚絵に描き、data URL（PNG）で返す。
 *
 * 適用後の見た目と一致させるため、変換は generatedMapToModels を通す。
 */
/**
 * 記録の区切りのマップを 1 枚絵に描き、data URL（PNG）で返す（キャプチャを持たない昔の区切りのカード用）。
 * 背景はキャプチャと同じダークグレー
 */
export const renderMapModelsPreview = (
  scene: Scene,
  models: MapModel[],
  size: { x: number; z: number },
): Promise<string> => {
  const task = queue.then(async () => {
    const previewScene = new Scene(scene.getEngine());
    previewScene.clearColor = new Color4(84 / 255, 84 / 255, 84 / 255, 1); // #545454
    try {
      const camera = await buildModelsScene(previewScene, models, size, true);
      await previewScene.whenReadyAsync();
      return await CreateScreenshotUsingRenderTargetAsync(scene.getEngine(), camera, {
        width: PREVIEW_WIDTH,
        height: PREVIEW_HEIGHT,
      });
    } finally {
      previewScene.dispose();
    }
  });
  queue = task.catch(() => undefined);
  return task;
};

export const renderGeneratedMapPreview = (
  scene: Scene,
  data: GeneratedMapData,
  size: { x: number; z: number },
): Promise<string> => {
  const task = queue.then(() => render(scene, data, size));
  queue = task.catch(() => undefined);
  return task;
};
